import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Send, ArrowUp, Loader2 } from 'lucide-react';
import PhoneInput from './PhoneInput';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Website-Chatblase vorerst abgeschaltet (Kundenwunsch 10/2026) – zum Reaktivieren auf true setzen.
const CHAT_BLASE_AKTIV = false;

const ChatWidget = () => {
  const { isAuthenticated } = useAuth();
  return CHAT_BLASE_AKTIV && isAuthenticated ? <ChatWidgetInner /> : null;
};

const ChatWidgetInner = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: 'bot', text: t('chat.greeting', 'Hallo! Schön, dass Sie da sind. Bitte hinterlassen Sie kurz Ihre Kontaktdaten und Ihre Nachricht – wir melden uns persönlich bei Ihnen.') },
  ]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const send = async () => {
    if (!input.trim()) return;
    if (!name.trim()) {
      setMessages((m) => [...m, { from: 'bot', text: t('chat.err_name', 'Bitte tragen Sie kurz Ihren Namen ein, damit wir Sie ansprechen können.') }]);
      return;
    }
    if (!validEmail(email)) {
      setMessages((m) => [...m, { from: 'bot', text: t('chat.err_email', 'Bitte hinterlassen Sie eine gültige E-Mail-Adresse, damit wir Ihnen antworten können.') }]);
      return;
    }
    setMessages((m) => [...m, { from: 'user', text: input }]);
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('message', input);
      fd.append('name', name);
      fd.append('email', email);
      fd.append('phone', phone);
      await axios.post(`${API}/contact/chat`, fd);
      setInput('');
      setSent(true);
      setMessages((m) => [...m, { from: 'bot', text: t('chat.success', 'Vielen Dank! Ihre Nachricht ist bei uns eingegangen. Wir melden uns in Kürze unter {{email}}.', { email }) }]);
    } catch (err) {
      setMessages((m) => [...m, { from: 'bot', text: t('chat.error', 'Leider gab es ein technisches Problem. Bitte versuchen Sie es erneut oder rufen Sie uns unter +49 228 30 40 640 an.') }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-80 bg-white rounded-sm shadow-2xl overflow-hidden border border-gray-200 animate-in slide-in-from-bottom-4 duration-300">
          <div className="bg-[#1B3A5C] text-white px-5 py-4 flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">{t('chat.title', 'Wollen wir chatten?')}</p>
              <p className="text-xs text-white/70">{t('chat.subtitle', 'Wir melden uns persönlich')}</p>
            </div>
            <button onClick={() => setOpen(false)} className="hover:bg-white/10 p-1 rounded" aria-label={t('common.close', 'Schließen')}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="h-60 overflow-y-auto p-4 bg-[#f5f8fa] space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={m.id || `${m.from}-${i}`} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-sm text-sm ${
                    m.from === 'user'
                      ? 'bg-[#1B3A5C] text-white'
                      : 'bg-white text-[#34536b] shadow-sm border border-gray-100'
                  }`}>{m.text}</div>
              </div>
            ))}
          </div>
          {!sent && (
            <div className="px-3 pt-3 bg-white border-t space-y-2">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('chat.ph_name', 'Ihr Name *')}
                className="w-full px-3 py-2 text-sm bg-[#f5f8fa] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#b8893d]/40"
                data-testid="chat-name"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('chat.ph_email', 'E-Mail *')}
                className="w-full px-3 py-2 text-sm bg-[#f5f8fa] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#b8893d]/40"
                data-testid="chat-email"
              />
              <PhoneInput
                value={phone}
                onChange={(v) => setPhone(v)}
                placeholder={t('chat.ph_phone_ph', '176 1234567 (optional)')}
                testId="chat-phone"
                inputClassName="px-3 py-2 bg-[#f5f8fa] focus:ring-2 focus:ring-[#b8893d]/40"
              />
            </div>
          )}
          <div className="p-3 bg-white border-t flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !sending && send()}
              placeholder={sent ? t('chat.ph_followup', 'Weitere Nachricht…') : t('chat.ph_message', 'Ihre Nachricht *')}
              className="flex-1 px-3 py-2 text-sm bg-[#f5f8fa] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#b8893d]/40"
              data-testid="chat-input"
              disabled={sending}
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              data-testid="chat-send"
              className="w-9 h-9 bg-[#b8893d] hover:bg-[#a07730] disabled:opacity-50 text-white rounded-sm flex items-center justify-center transition-colors"
              aria-label={t('common.submit', 'Senden')}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle"
        className="fixed bottom-6 right-6 z-50 bg-[#1B3A5C] hover:bg-[#0f2740] text-white px-5 py-3 rounded-sm shadow-xl flex items-center gap-2 transition-all hover:scale-105"
      >
        <MessageCircle className="w-4 h-4" />
        <span className="text-sm font-medium">{t('chat.title', 'Wollen wir chatten?')}</span>
      </button>
    </>
  );
};

const BackToTop = () => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!show) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-24 left-6 z-40 w-11 h-11 bg-[#b8893d] hover:bg-[#a07730] text-white rounded-sm shadow-lg flex items-center justify-center transition-all hover:-translate-y-1"
      aria-label={t('common.back_to_top', 'Zurück nach oben')}
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
};

export { ChatWidget, BackToTop };
